import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button'
import CardPaginator from '../../../components/ui/CardPaginator'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import FilterBar from '../../../components/ui/FilterBar'
import { useToast } from '../../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../../api/apiError'
import { formatDate } from '../../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { formatDuration } from '../duration'
import { useFlightRecords } from '../hooks/useTimePortfolio'
import { flightRecordColumns } from './FlightRecordColumns'
import FlightRecordFormDialog from './FlightRecordFormDialog'
import type { FlightRecord, FlightRecordUpsert } from '../types'

/** Sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/** Arama gecikmesi — her tuşta sunucuya gidilmez. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * **Zaman Portföyü → Uçuş Kaydı** sekmesi. Kardeş sekmenin sadeleşmiş hâlidir: tür/kategori süzgeci
 * yoktur, arama yalnız açıklamadadır ve süre kullanıcıdan alınır (hesaplanmaz).
 *
 * <p>Sayfalama/süzme kuralları ve dar ekran kart kalıbı kardeş sekmeyle aynıdır.
 */
export default function FlightRecordsTab() {
  const { t } = useTranslation()
  const toast = useToast()
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<FlightRecord | null>(null)

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search.trim())
      setFirst(0)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  // Tarih aralığı süzgeci ekrandan KALDIRILDI (kullanıcı isteği 23.09.2026); uç parametreleri
  // opsiyonel olarak duruyor.
  const query = useMemo(
    () => ({
      q: debounced || null,
      page: Math.floor(first / rows),
      size: rows,
    }),
    [debounced, first, rows],
  )

  const { list, create, update, remove } = useFlightRecords(query)

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  const submitting = create.isPending || update.isPending
  const busyId = remove.isPending ? (remove.variables ?? null) : null

  const durationLabels = {
    hour: t('timePortfolio.duration.hour'),
    minute: t('timePortfolio.duration.minute'),
  }

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (row: FlightRecord) => {
    setEditing(row)
    setFormOpen(true)
  }

  const handleMutationError = (err: unknown) => {
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: FlightRecordUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('timePortfolio.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
      return
    }
    create.mutate(payload, {
      onSuccess: () => {
        notify('success', t('timePortfolio.toast.created'))
        setFormOpen(false)
        setFirst(0)
      },
      onError: handleMutationError,
    })
  }

  const onDelete = (row: FlightRecord) => {
    confirmDialog({
      header: t('timePortfolio.delete.title'),
      message: t('timePortfolio.delete.confirm', { name: formatDate(row.flightDate) }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('timePortfolio.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('timePortfolio.toast.deleted')),
        }),
    })
  }

  const empty = (
    <EmptyState
      title={t('timePortfolio.flight.empty.title')}
      description={t('timePortfolio.flight.empty.desc')}
    />
  )

  return (
    <div className="tp-tab">
      <FlightRecordFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <div className="tp-tab-actions">
        <Button
          icon="pi pi-plus"
          label={t('timePortfolio.flight.actions.new')}
          onClick={openCreate}
        />
      </div>

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('timePortfolio.flight.search')}
        onSearch={setSearch}
        onRefresh={() => list.refetch()}
      />

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && empty}
          {!list.isLoading &&
            items.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <span className="ui-card-sub">{formatDate(row.flightDate)}</span>
                  <div className="ui-card-actions ui-card-actions--compact">
                    <Button
                      icon="pi pi-pencil"
                      rounded
                      text
                      aria-label={t('timePortfolio.actions.editNamed', {
                        name: formatDate(row.flightDate),
                      })}
                      onClick={() => openEdit(row)}
                    />
                    <Button
                      icon="pi pi-trash"
                      rounded
                      text
                      severity="danger"
                      aria-label={t('timePortfolio.actions.deleteNamed', {
                        name: formatDate(row.flightDate),
                      })}
                      disabled={busyId === row.id}
                      onClick={() => onDelete(row)}
                    />
                  </div>
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">
                    {formatDuration(row.durationMinutes, durationLabels)}
                  </span>
                </div>
                {row.description && <span className="ui-card-sub">{row.description}</span>}
              </div>
            ))}
          {!list.isLoading && total > rows && (
            <CardPaginator first={first} rows={rows} totalRecords={total} onPage={setFirst} />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          lazy
          first={first}
          rows={rows}
          totalRecords={total}
          onPage={(e) => {
            setFirst(e.first)
            setRows(e.rows)
          }}
          emptyMessage={empty}
        >
          {flightRecordColumns({ t, busyId, onEdit: openEdit, onDelete })}
        </DataTable>
      )}
    </div>
  )
}
